// Wrapper-side OAuth `auth-required` navigator + provider picker.
//
// The SW's `routeThroughWrapper` (`sw/core.ts`) emits a
// `p2claw_auth_required` message when an auth-gated app (one whose
// `requires_auth=true` flag is set) returns the daemon middleware's
// `401 + P2claw-Auth-Required: true` response. The 401 itself
// is forwarded to the page verbatim — apps with their own retry-on-
// 401 path see it normally — but the wrapper additionally renders an
// overlay asking the user which identity provider to sign in with.
// After provider pick → broker → upstream → broker → `/__p2claw/oauth/land`
// the browser lands back at the original request URL with
// `__p2claw_session` attached, and the daemon's middleware lets it
// through.
//
// De-dup on this side: the SW can fire `p2claw_auth_required` once
// per failing request, and a fresh-load page can issue dozens of
// concurrent subresource fetches that all 401. A `prompted` flag
// guarantees we only mount ONE overlay per page lifetime; subsequent
// messages are ignored (the user is already presented with a
// picker, no point re-rendering).

import type { SwContainerLike } from "./sw-handoff.js";

/**
 * Compile-time-substituted default OAuth broker. Defined by vite
 * via `define: __P2CLAW_BROKER_BASE_URL__`. Self-hosters running
 * their own broker set `VITE_P2CLAW_OAUTH_BROKER_URL` at build
 * time; the broker spec () names this
 * as the `P2CLAW_OAUTH_BROKER_PUBLIC_URL` config key. Empty string
 * at runtime means the build had no env var — fall back to the
 * canonical default. Per-call callers can also pass `brokerBaseUrl`
 * to `installAuthRequiredListener` to override on the spot.
 */
declare const __P2CLAW_BROKER_BASE_URL__: string;

function readBuildTimeBrokerUrl(): string {
  // Vite substitutes the define at build time; vitest imports the
  // source directly and the identifier is undefined. Read defensively
  // so tests + dev mode fall back to the canonical default.
  try {
    return __P2CLAW_BROKER_BASE_URL__;
  } catch {
    return "";
  }
}

export const DEFAULT_BROKER_BASE_URL: string =
  readBuildTimeBrokerUrl() !== ""
    ? readBuildTimeBrokerUrl()
    : "https://oauth.p2claw.com";

/** Provider id + the human-readable label rendered on its button.
 *  Hardcoded here because the broker doesn't expose a `/providers`
 *  endpoint yet; if a provider in this list isn't actually
 *  configured on the operator's broker the click will land at
 *  `/authorize` and the broker 400s with "provider not configured".
 *  Worth a follow-up (have the broker advertise the active providers
 *  + filter this list client-side). */
export interface ProviderChoice {
  id: string;
  label: string;
}

export const DEFAULT_PROVIDERS: ReadonlyArray<ProviderChoice> = [
  { id: "github", label: "Sign in with GitHub" },
  { id: "google", label: "Sign in with Google" },
];

/** Back-compat alias — kept so any caller that used to import
 *  `DEFAULT_PROVIDER` to skip the picker still resolves. The picker
 *  is shown unconditionally now; callers wanting headless behaviour
 *  build the URL via `buildBrokerAuthorizeUrl` themselves. */
export const DEFAULT_PROVIDER = "github";

export interface AuthRequiredPromptOptions {
  /** Same shape as `installSwHandoff`'s `navigatorSw` — usually
   *  `navigator.serviceWorker`. */
  navigatorSw: SwContainerLike;
  /** App label from the parsed hostname (e.g. `recipes`, `null`
   *  for apex-form). `null` collapses to an empty `app=` param in
   *  the broker URL; the broker tolerates that because the `aud`
   *  is keyed on `peer_id` (it doesn't strictly need the app
   *  label, just the alias + peer_id for context). */
  appLabel: string | null;
  /** Alias from the parsed hostname (haiku or vanity). */
  aliasLabel: string;
  /** Z-base-32-encoded `peer_id` from the wrapper's meta tags. */
  peerIdZ32: string;
  /** Override the broker base URL (test seam; operators of self-
   *  hosted broker deploys). Defaults to [`DEFAULT_BROKER_BASE_URL`]. */
  brokerBaseUrl?: string;
  /** Provider list rendered on the picker. Defaults to
   *  [`DEFAULT_PROVIDERS`] (GitHub + Google). */
  providers?: ReadonlyArray<ProviderChoice>;
  /** Test seam — defaults to `window.location.assign(url)`. */
  navigate?: (url: string) => void;
  /** Test seam — defaults to mounting a DOM overlay on `document.body`.
   *  Called once with the list of provider choices and a `navigate`
   *  fn the seam should invoke when the user picks one. */
  renderPicker?: (
    choices: Array<{ id: string; label: string; url: string }>,
    navigate: (url: string) => void,
  ) => void;
}

export interface AuthRequiredPromptHandle {
  /** Tear down the listener. Idempotent. */
  dispose(): void;
}

/** Recognise the SW's auth-required ping. Loose runtime type
 *  guard — the SW could in principle send other messages on the
 *  same channel (`p2claw_sw_wake`, `p2claw_basic_auth_needed`),
 *  and we don't want to react to those. */
function isAuthRequiredMessage(
  d: unknown,
): d is { type: string; returnUrl: string } {
  if (!d || typeof d !== "object") return false;
  const r = d as { type?: unknown; returnUrl?: unknown };
  return r.type === "p2claw_auth_required" && typeof r.returnUrl === "string";
}

/**
 * Build the broker `/authorize` URL per .
 * Query parameters:
 *
 *   - `app`    — the app label (`recipes`, etc.). May be the empty
 *                string for apex-form hostnames; broker tolerates
 *                empty.
 *   - `alias`  — the alias label (haiku or vanity).
 *   - `peer_id`— z-base-32 `peer_id` so the broker mints a JWT
 *                whose `aud` matches the destination box.
 *   - `return` — the URL the user should land on after sign-in.
 *                Encoded automatically by URLSearchParams.
 *   - `provider` — `github` / `google` / `apple`.
 *
 * Exported for unit-test convenience.
 */
export function buildBrokerAuthorizeUrl(input: {
  brokerBaseUrl: string;
  appLabel: string | null;
  aliasLabel: string;
  peerIdZ32: string;
  returnUrl: string;
  provider: string;
}): string {
  const u = new URL("/authorize", input.brokerBaseUrl);
  u.searchParams.set("app", input.appLabel ?? "");
  u.searchParams.set("alias", input.aliasLabel);
  u.searchParams.set("peer_id", input.peerIdZ32);
  u.searchParams.set("return", input.returnUrl);
  u.searchParams.set("provider", input.provider);
  return u.toString();
}

function defaultNavigate(url: string): void {
  if (typeof window !== "undefined") {
    window.location.assign(url);
  }
}

/**
 * Default picker — minimal centered overlay with a button per
 * provider plus a close affordance in the top-right. Inlines all
 * styles so the overlay renders identically whether or not the
 * app's own CSS has loaded.
 *
 * Mounted on `document.body`. Two dismiss paths:
 *   - User picks a provider → click handler navigates the page,
 *     which tears down the document and the overlay with it.
 *   - User clicks the close ✕ → overlay element is removed from
 *     `document.body`. Useful when the auth-required ping fired
 *     on a stale 401 (e.g. a subresource that raced ahead of the
 *     session cookie being attached) but the user is in fact
 *     already signed in — the page behind the overlay is rendering
 *     fine, and they just want to dismiss it.
 *
 * The dismiss button is intentionally not labelled "Cancel" — the
 * implication that picking a provider is the "primary" action and
 * closing is the "secondary" is correct for the cold-load case
 * (visitor lands on an auth-gated app and hasn't signed in yet).
 */
function defaultRenderPicker(
  choices: Array<{ id: string; label: string; url: string }>,
  navigate: (url: string) => void,
): void {
  if (typeof document === "undefined") return;
  const overlay = document.createElement("div");
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Sign in to continue");
  // Fully opaque backdrop — the daemon middleware returns the 401
  // with a plain-text body ("authentication required (accepted
  // methods: oauth)") and an earlier translucent dim let that text
  // bleed through in the top-left while the picker was mounted.
  // Solid dark blocks the iframe content entirely; on dismiss
  // (close ✕ or cookie auto-arrival) the page re-emerges intact.
  overlay.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483647",
    "background:#1a1a1a",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "font-family:system-ui,-apple-system,sans-serif",
  ].join(";");

  const card = document.createElement("div");
  card.style.cssText = [
    "background:#fbf9f3",
    "color:#111",
    "border-radius:14px",
    "padding:32px 36px",
    "max-width:380px",
    "width:calc(100% - 32px)",
    "box-shadow:0 24px 60px rgba(0,0,0,0.35)",
    "display:flex",
    "flex-direction:column",
    "gap:18px",
    "position:relative",
  ].join(";");

  // Top-right close button — dismisses the overlay without
  // navigating. See function-doc for when this is useful.
  const close = document.createElement("button");
  close.type = "button";
  close.setAttribute("aria-label", "Close");
  close.textContent = "×"; // ×
  close.style.cssText = [
    "position:absolute",
    "top:10px",
    "right:14px",
    "width:28px",
    "height:28px",
    "padding:0",
    "background:transparent",
    "border:none",
    "color:#999",
    "font-size:22px",
    "line-height:1",
    "cursor:pointer",
    "border-radius:6px",
  ].join(";");
  close.addEventListener("click", () => overlay.remove());
  card.appendChild(close);

  const title = document.createElement("h2");
  title.textContent = "Sign in to continue";
  title.style.cssText = "margin:0;font-size:20px;font-weight:600;letter-spacing:-0.01em";
  card.appendChild(title);

  const sub = document.createElement("p");
  sub.textContent = "This app requires sign-in. Pick a provider:";
  sub.style.cssText = "margin:0;font-size:14px;color:#555;line-height:1.45";
  card.appendChild(sub);

  for (const choice of choices) {
    const a = document.createElement("a");
    a.href = choice.url;
    a.textContent = choice.label;
    a.dataset.provider = choice.id;
    a.style.cssText = [
      "display:block",
      "padding:11px 16px",
      "background:#111",
      "color:#fbf9f3",
      "text-align:center",
      "border-radius:10px",
      "font-size:14px",
      "font-weight:500",
      "text-decoration:none",
      "letter-spacing:0.01em",
    ].join(";");
    a.addEventListener("click", (ev) => {
      ev.preventDefault();
      navigate(choice.url);
    });
    card.appendChild(a);
  }

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // Auto-dismiss if we observe that a session cookie is actually
  // present — covers the "stale 401 raced the cookie" case where
  // the overlay mounts but the page behind it is in fact rendering
  // fine because the cookie did arrive in time for the document
  // load. cookieStore is Chromium/Safari-TP only; on Firefox the
  // check silently no-ops and the user dismisses via the ✕.
  void maybeAutoDismissOnCookieArrival(overlay);
}

/**
 * Auto-dismiss the picker overlay when the OAuth session cookie
 * becomes readable. Two parallel mechanisms:
 *
 *   1. Subscribe to `cookieStore.addEventListener("change", ...)`
 *      so the moment the cookie is set we react immediately
 *      without polling latency. Covers the post-OAuth-redirect
 *      case where the picker mounted on a stale 401 ping but the
 *      cookie arrives milliseconds later.
 *   2. Poll `cookieStore.get(...)` every 250ms for up to 30s as
 *      a backstop. Covers the "cookie was already there at picker
 *      mount" case (`change` event won't fire) and any timing
 *      window where `change` was missed before the listener
 *      attached.
 *
 * Either path can fire first; both call `dismiss()` which is
 * idempotent (removing an already-detached element is a no-op).
 *
 * Transient `cookieStore.get` throws (extension interference,
 * partitioned-context warnings on first call) do NOT abort the
 * helper — they get logged once and the loop continues. An
 * earlier version returned on the first throw and left the
 * overlay stuck.
 */
async function maybeAutoDismissOnCookieArrival(overlay: HTMLElement): Promise<void> {
  const cs = (globalThis as {
    cookieStore?: {
      get: (n: string) => Promise<{ value: string } | null>;
      addEventListener: (
        type: "change",
        cb: (ev: { changed?: Array<{ name: string; value: string }>; deleted?: Array<{ name: string }> }) => void,
      ) => void;
    };
  }).cookieStore;
  if (!cs) {
    console.warn("[p2claw:auth-prompt] cookieStore unsupported; picker won't auto-dismiss");
    return;
  }
  console.info("[p2claw:auth-prompt] auto-dismiss watcher armed");
  let dismissed = false;
  const dismiss = (reason: string): void => {
    if (dismissed) return;
    dismissed = true;
    overlay.remove();
    console.info("[p2claw:auth-prompt] auto-dismissed:", reason);
  };
  // (1) Event-driven path — fires the moment the cookie is set.
  try {
    cs.addEventListener("change", (ev) => {
      const hit = ev.changed?.find((c) => c.name === "__p2claw_session" && c.value);
      if (hit) dismiss("cookieStore change event");
    });
  } catch (e) {
    console.warn("[p2claw:auth-prompt] cookieStore.addEventListener failed:", e);
  }
  // (2) Poll path — covers "already present" + missed-event windows.
  const deadline = Date.now() + 30000;
  let lastErr: unknown = null;
  while (!dismissed && Date.now() < deadline) {
    try {
      const c = await cs.get("__p2claw_session");
      if (c && typeof c.value === "string" && c.value.length > 0) {
        dismiss("cookieStore.get poll");
        return;
      }
    } catch (e) {
      if (String(e) !== String(lastErr)) {
        console.warn("[p2claw:auth-prompt] cookieStore.get failed; will retry:", e);
        lastErr = e;
      }
    }
    await new Promise((r) => setTimeout(r, 250));
  }
}

/**
 * Install the wrapper-side listener. Returns a handle whose
 * `dispose()` removes the listener — used by the wrapper's failure
 * teardown to keep the listener from leaking past a re-handshake
 * or boot-error overlay.
 *
 * On the FIRST `p2claw_auth_required` message in the page's
 * lifetime: build a broker URL per provider and call `renderPicker`.
 * Subsequent messages are ignored (we already showed a picker, the
 * user is choosing).
 */
export function installAuthRequiredListener(
  opts: AuthRequiredPromptOptions,
): AuthRequiredPromptHandle {
  let prompted = false;
  let disposed = false;
  const brokerBaseUrl = opts.brokerBaseUrl ?? DEFAULT_BROKER_BASE_URL;
  const providers = opts.providers ?? DEFAULT_PROVIDERS;
  const navigate = opts.navigate ?? defaultNavigate;
  const renderPicker = opts.renderPicker ?? defaultRenderPicker;

  const onMessage = (ev: { data: unknown }): void => {
    if (disposed) return;
    if (!isAuthRequiredMessage(ev.data)) return;
    if (prompted) return;
    prompted = true;
    // Snapshot the narrowed message — TypeScript drops type-guard
    // narrowing on property access inside nested callbacks like `.map`.
    const message = ev.data;
    const choices = providers.map((p) => ({
      id: p.id,
      label: p.label,
      url: buildBrokerAuthorizeUrl({
        brokerBaseUrl,
        appLabel: opts.appLabel,
        aliasLabel: opts.aliasLabel,
        peerIdZ32: opts.peerIdZ32,
        returnUrl: message.returnUrl,
        provider: p.id,
      }),
    }));
    renderPicker(choices, navigate);
  };

  opts.navigatorSw.addEventListener("message", onMessage);

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      opts.navigatorSw.removeEventListener("message", onMessage);
    },
  };
}
